import { useState } from 'react';
import { createCharacter, setCharacterPin, type Character, ApiError } from '../../lib/api';
import { type IconValue } from './IconAvatar';
import { Character3DHero } from './Character3DHero';
import { IconPicker } from './IconPicker';
import { PinPad } from './PinPad';
import type { CosmeticInventory } from '../../lib/api';

interface Props {
  onCancel: () => void;
  onCreated: (c: Character) => void;
}

const REASONS: Record<string, string> = {
  'name-too-short': 'Name is too short.',
  'name-too-long': 'Name is too long (20 max).',
  'name-bad-chars': 'Letters, numbers, spaces only.',
  'name-blocked': 'Try a different name.',
  'bad-icon': 'Pick a face and a color.',
  'collision': 'That id collided. Try again.',
  'locked-cosmetic': 'That part unlocks after playing.',
};

// R2#65: keep this in sync with DEFAULT_INVENTORY in
// lambdas/characters/src/index.ts and lambdas/leaderboards/src/index.ts.
// We don't have a shared module for these defaults yet (the lambdas and
// the web app don't share a common package for starter inventory), so
// this is a hand-mirrored copy. Without `palettes: ['palette-dusk']`,
// the IconPicker palette tab is locked at character creation even though
// the server grants the dusk palette by default.
const STARTER_INVENTORY: CosmeticInventory = {
  bodies: [0, 1],
  hats: [0],
  faces: [0, 1, 2],
  hues: [0, 25, 50, 180, 250],
  accentHues: [0, 25, 50, 180, 250],
  titles: ['Tavern Regular'],
  palettes: ['palette-dusk'],
};

export function CharacterCreate({ onCancel, onCreated }: Props) {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<IconValue>({ set: 'tavern-2', id: 0, hue: 25, body: 0, hat: 0, face: 0, accentHue: 50 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Stage 2 — after createCharacter succeeds, show a prominent PIN-set
  // step before navigating away. Setting a PIN at creation time keeps
  // the character recoverable across devices; we don't want users to
  // discover this only as a slim ribbon on the home screen.
  const [created, setCreated] = useState<Character | null>(null);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const c = await createCharacter(name, icon);
      setCreated(c);
      setBusy(false);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'unknown';
      setErr(REASONS[code] ?? 'Something went wrong.');
      setBusy(false);
    }
  }

  async function submitPin(pin: string) {
    if (!created) return;
    setPinBusy(true);
    setPinError(null);
    try {
      await setCharacterPin(created.charId, pin);
      onCreated({ ...created, hasPin: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'pin-already-set') {
        // A PIN already exists (double-submit race) — that IS the goal
        // state, and the step is mandatory, so don't strand the player.
        onCreated({ ...created, hasPin: true });
        return;
      }
      setPinError('Could not set PIN. Try again.');
      setPinBusy(false);
    }
  }


  return (
    <main className="create-screen">
      <header className="create-head">
        <h1 className="display create-title">Forge your character</h1>
        <p className="muted create-subtitle">They'll be walking the hall in a minute.</p>
      </header>
      <form onSubmit={submit} className="create-form" noValidate>
        <div className="create-layout">
          {/* Live 3D stage — the player sees the SAME figure they'll play
              as while building it; it updates instantly as parts change.
              `controls` gives the play/pause + maximize inspect chrome the
              Locker hero uses. Reuses the lazy Character3DHero wrapper so
              Three.js stays code-split and out of the main bundle. */}
          <aside className="create-stage">
            <div className="create-hero3d">
              {/* NPE-3: the name floats above the figure as a REAL nameplate
                  while they type — the "names above heads" law starts here. */}
              <div className={`create-nameplate${name.trim() ? '' : ' is-ghost'}`} aria-hidden="true">
                {name.trim() || '…?'}
              </div>
              <Character3DHero icon={icon} size={300} controls warmStage />
            </div>
            <label htmlFor="create-name-input" className="create-name-label">Name</label>
            <input
              id="create-name-input"
              type="text"
              maxLength={20}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="What do they call you?"
              className="create-name"
              autoFocus
              aria-label="character name"
            />
            <p className="create-hint muted">Drag to spin · tap the frame icon to inspect up close</p>
          </aside>

          <div className="create-builder-panel">
            <IconPicker value={icon} onChange={setIcon} inventory={STARTER_INVENTORY} variant="create" />
          </div>
        </div>

        {err && <div role="alert" className="gate-error">{err}</div>}

        <div className="create-actions">
          <button type="button" className="btn ghost big" onClick={onCancel} disabled={busy}>Back</button>
          <button type="submit" className="btn primary big" disabled={busy || name.trim().length < 2}>
            {busy ? 'Stepping in…' : 'Step into the tavern'}
          </button>
        </div>
      </form>
      {created && (
        <PinPad
          mode="set"
          title={`Lock ${created.name} with a PIN`}
          subtitle={`4 digits. It's how ${created.name} stays yours on any device.`}
          error={pinError}
          busy={pinBusy}
          mandatory
          onCancel={() => {}}
          onSubmit={(pin) => void submitPin(pin)}
        />
      )}
    </main>
  );
}
